"use client";

import React, { useState, useCallback } from 'react';
import styles from './_hcp-bsa-calculator.module.scss';
import Button from '@/components/button';
import CustomImage from '@/components/image';
import PageAnchor from '@/components/page-anchor';
import Badge from '@/components/badge';

import BsaStepInputs from './steps/bsa-step-inputs';

/**
 * @description HCP Dosing — Body Surface Area Calculator
 * Inline single-step calculator that lets HCPs estimate a patient's BSA
 * using height and weight inputs (DuBois formula).
 *
 * Intro screen mirrors the Figma design (node 14429:49819):
 *   left — for-export graphic; right — title, description, CTA button.
 *
 * Data / formula:  src/data/bsa-calculator-data.js
 */

const INITIAL_FORM = {};

function HcpBodySurfaceAreaCalculator({ ref, className }) {
  const [hasStarted, setHasStarted] = useState(false);
  const [formValues, setFormValues] = useState(INITIAL_FORM);

  const sectionClass = `${styles.bsa_calculator}${hasStarted ? ` ${styles.bsa_calculator__active}` : ''}${className ? ` ${className}` : ''}`;

  const handleStart = useCallback(() => {
    setHasStarted(true);
  }, []);

  const handleNext = useCallback((values) => {
    setFormValues(values);
  }, []);

  const handleReset = useCallback(() => {
    setHasStarted(false);
    setFormValues(INITIAL_FORM);
  }, []);

  return (
    <>
      <PageAnchor anchorId="bsa-calculator" offsetY={-32} />
      <section
        ref={ref}
        className={sectionClass}
        data-gtm-event-category="bsa-calculator"
      >
        <div className="container container--px-16">
          <div className={`${styles.calculator_layout}${hasStarted ? ` ${styles.calculator_layout__step}` : ''}`}>

            {/* ── Image column — always visible ── */}
            <div className={styles.image_col}>
              <CustomImage
                src="/images/components/hcp/body-surface-area-calc/Dosing_Body-Surface-Area-Calculator.png"
                alt=""
                width={382}
                height={365}
                className={styles.graphic}
              />
            </div>

            {/* ── Content column ── */}
            <div className={styles.content_col}>
              <Badge ariaLabel="New">NEW</Badge>

              {!hasStarted ? (
                // ── Intro / default state ──────────────────────────────
                <div className={styles.intro_content}>
                  <h2 className={styles.intro_heading}>
                    Body Surface Area Calculator
                  </h2>

                  <p className={styles.intro_description}>
                    When making changes to your patient&rsquo;s GC regimen,
                    the Body Surface Area Calculator can help determine your
                    patient&rsquo;s new GC dose. Simply input their height and
                    weight to get the results.
                  </p>

                  <Button
                    type="button"
                    variant={['solid', 'solid--eggplant', 'hcp-big']}
                    onClick={handleStart}
                    data-gtm-event-category="bsa-calculator"
                    data-gtm-event-action="click"
                    data-gtm-event-label="Calculate BSA"
                  >
                    Calculate now
                  </Button>
                </div>
              ) : (
                // ── Calculator step ────────────────────────────────────
                <div className={styles.form_card}>
                  <div className={styles.form_card_header}>
                    <h2 className={styles.form_card_title}>
                      Body Surface Area Calculator
                    </h2>
                    <p className={styles.form_card_description}>
                      Body surface area can be an important consideration when
                      determining physiologic GC doses for cortisol replacement.
                    </p>
                  </div>
                  <BsaStepInputs
                    formValues={formValues}
                    onNext={handleNext}
                    onReset={handleReset}
                  />
                </div>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

export default HcpBodySurfaceAreaCalculator;
